/**
 * How overdue a backup is, and how loudly to say so (T-503).
 *
 * The third durability mechanism from ADR-0003. Export and import exist; this
 * is what makes people use them, because backup discipline sustained over
 * months without prompting is not a realistic expectation and the cost of
 * forgetting is total.
 *
 * Tone is the whole design problem, and it is a domain decision rather than a
 * styling one — which is why the escalation lives here and not in a component.
 * Too quiet and it is ignored until it matters; too loud and it gets dismissed
 * reflexively, which is worse, because that trains the habit of dismissing.
 * What carries the weight is a concrete number: "94 entries since your last
 * backup" is a sentence about something the user actually did.
 */

import type { Timestamp } from '../model/timestamp.js'
import type { Clock } from '../ports/clock.js'
import type { Storage } from '../ports/storage.js'

export type BackupUrgency = 'none' | 'gentle' | 'firm' | 'urgent'

const RANK: Readonly<Record<BackupUrgency, number>> = {
  none: 0,
  gentle: 1,
  firm: 2,
  urgent: 3,
}

export interface BackupRecord {
  readonly at: Timestamp
  /** How many entries existed at that moment, so "since" is a real number. */
  readonly entryCount: number
}

export interface BackupDismissal {
  readonly at: Timestamp
  /** The level that was dismissed. A higher one is a different message. */
  readonly urgency: BackupUrgency
}

export interface BackupThresholds {
  readonly gentleDays: number
  readonly firmDays: number
  readonly urgentDays: number
  /** Unbacked-up entries that make it urgent regardless of elapsed time. */
  readonly urgentEntries: number
}

export const DEFAULT_BACKUP_THRESHOLDS: BackupThresholds = {
  gentleDays: 7,
  firmDays: 14,
  urgentDays: 30,
  urgentEntries: 100,
}

/**
 * Every threshold is halved when the browser may evict the data.
 *
 * `persistent` only means "not evicted to reclaim space"; without it, Safari
 * discards script-writable storage after roughly seven days of no interaction
 * for a site that is not installed (ADR-0003). Waiting the full thirty days to
 * become insistent in that regime would mean the loud message arrives after the
 * data is already gone.
 */
const AT_RISK_FACTOR = 0.5

/** How long an `urgent` dismissal buys before the prompt returns. */
const URGENT_SNOOZE_DAYS = 1

const DAY_MS = 86_400_000

export interface BackupHealthInput {
  readonly now: Timestamp
  readonly entryCount: number
  readonly last?: BackupRecord
  /**
   * When recording started, used as the baseline when nothing has ever been
   * backed up. Measuring from the first entry rather than from an install date
   * means "you have been recording for three weeks with no backup" is true.
   */
  readonly firstEntryAt?: Timestamp
  readonly dismissal?: BackupDismissal
  /** Whether the browser may throw this data away. See `AT_RISK_FACTOR`. */
  readonly evictable: boolean
  readonly thresholds?: BackupThresholds
}

export interface BackupHealth {
  readonly urgency: BackupUrgency
  /** Whether to show it at all, once a dismissal is taken into account. */
  readonly visible: boolean
  /** Elapsed days since the last backup, or since recording started. */
  readonly daysSince: number
  readonly entriesSince: number
  readonly everBackedUp: boolean
  /** The number that carries the weight. Safe to show as-is. */
  readonly headline: string
  readonly detail: string
}

function elapsedDays(from: Timestamp, to: Timestamp): number {
  // Elapsed rather than calendar days, deliberately: this is a duration, not a
  // position in anyone's week, so it is unaffected by DST and by travel.
  return Math.max(0, Math.floor((to.epochMs - from.epochMs) / DAY_MS))
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${String(count)} ${count === 1 ? one : many}`
}

export function backupHealth(input: BackupHealthInput): BackupHealth {
  const thresholds = input.thresholds ?? DEFAULT_BACKUP_THRESHOLDS
  const everBackedUp = input.last !== undefined
  const baseline = input.last?.at ?? input.firstEntryAt
  const daysSince = baseline === undefined ? 0 : elapsedDays(baseline, input.now)
  const entriesSince = Math.max(0, input.entryCount - (input.last?.entryCount ?? 0))

  const urgency = urgencyOf({
    entryCount: input.entryCount,
    everBackedUp,
    entriesSince,
    daysSince,
    thresholds,
    evictable: input.evictable,
  })

  return {
    urgency,
    visible: urgency !== 'none' && !suppressed(urgency, input),
    daysSince,
    entriesSince,
    everBackedUp,
    ...describe({ everBackedUp, entriesSince, daysSince, evictable: input.evictable }),
  }
}

function urgencyOf(input: {
  readonly entryCount: number
  readonly everBackedUp: boolean
  readonly entriesSince: number
  readonly daysSince: number
  readonly thresholds: BackupThresholds
  readonly evictable: boolean
}): BackupUrgency {
  // Nothing recorded, nothing to lose. A warning on an empty database teaches
  // people that this prompt is noise, on the one screen where it must not be.
  if (input.entryCount === 0) {
    return 'none'
  }

  // Backed up, and nothing recorded since. The file on disk is current, however
  // long ago it was written — nagging here would be nagging about the calendar
  // rather than about risk.
  if (input.everBackedUp && input.entriesSince === 0) {
    return 'none'
  }

  const scale = input.evictable ? AT_RISK_FACTOR : 1
  const { gentleDays, firmDays, urgentDays, urgentEntries } = input.thresholds

  if (
    input.daysSince >= urgentDays * scale ||
    input.entriesSince >= urgentEntries * scale
  ) {
    return 'urgent'
  }
  if (input.daysSince >= firmDays * scale) {
    return 'firm'
  }
  if (input.daysSince >= gentleDays * scale) {
    return 'gentle'
  }
  return 'none'
}

/**
 * Whether a dismissal still hides this.
 *
 * A dismissal silences the level it was made at, not everything after it — the
 * prompt returning when things get worse is the whole point of having levels.
 * `urgent` is the exception the ticket asks for: it comes back regardless, but
 * not immediately, because a message that reappears the moment it is dismissed
 * is a message people learn to dismiss without reading.
 */
function suppressed(urgency: BackupUrgency, input: BackupHealthInput): boolean {
  const dismissal = input.dismissal
  if (dismissal === undefined) {
    return false
  }
  if (RANK[urgency] > RANK[dismissal.urgency]) {
    return false
  }
  if (urgency === 'urgent') {
    return elapsedDays(dismissal.at, input.now) < URGENT_SNOOZE_DAYS
  }
  return true
}

function describe(input: {
  readonly everBackedUp: boolean
  readonly entriesSince: number
  readonly daysSince: number
  readonly evictable: boolean
}): { readonly headline: string; readonly detail: string } {
  // Stated once, and only when it is true. Repeating it in every message is how
  // it stops being read.
  const risk = input.evictable
    ? ' This browser can clear it to reclaim space, without asking.'
    : ''

  if (!input.everBackedUp) {
    return {
      headline: `${plural(input.entriesSince, 'entry', 'entries')} recorded, never backed up`,
      detail: `Everything is on this device and nowhere else, so there is nothing to restore from.${risk}`,
    }
  }

  return {
    headline: `${plural(input.entriesSince, 'entry', 'entries')} since your last backup`,
    detail: `The last one was ${plural(input.daysSince, 'day')} ago.${risk}`,
  }
}

// ─── Persistence ──────────────────────────────────────────────────────────────

export const LAST_BACKUP_KEY = 'anytracker.lastBackup'
export const BACKUP_DISMISSED_KEY = 'anytracker.backupDismissed'

function readJson(raw: string | undefined): Record<string, unknown> | null {
  if (raw === undefined) {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    // Settings are strings, and a string can be anything. A nag that crashes
    // the home screen is worse than a nag that does not appear.
    return null
  }
}

function readTimestamp(value: unknown): Timestamp | null {
  if (typeof value !== 'object' || value === null) {
    return null
  }
  const record = value as Record<string, unknown>
  return typeof record['epochMs'] === 'number' && typeof record['timeZone'] === 'string'
    ? { epochMs: record['epochMs'], timeZone: record['timeZone'] }
    : null
}

export async function readBackupRecord(
  storage: Storage,
): Promise<BackupRecord | undefined> {
  const parsed = readJson(await storage.settings.get(LAST_BACKUP_KEY))
  const at = readTimestamp(parsed?.['at'])
  const entryCount = parsed?.['entryCount']
  if (at === null || typeof entryCount !== 'number') {
    return undefined
  }
  return { at, entryCount }
}

/**
 * Marks a backup as taken.
 *
 * Called only after a save actually succeeded. A cancelled download must not
 * reset this, or the app goes quiet about a backup that never happened — which
 * is exactly the failure `ExportResult` distinguishes cancelled from saved for.
 */
export function recordBackup(
  storage: Storage,
  record: { readonly at: Timestamp; readonly entryCount: number },
): Promise<void> {
  return storage.settings.set(LAST_BACKUP_KEY, JSON.stringify(record))
}

export function recordBackupNow(
  storage: Storage,
  clock: Clock,
  entryCount: number,
): Promise<void> {
  return recordBackup(storage, { at: clock.now(), entryCount })
}

export async function readDismissal(
  storage: Storage,
): Promise<BackupDismissal | undefined> {
  const parsed = readJson(await storage.settings.get(BACKUP_DISMISSED_KEY))
  const at = readTimestamp(parsed?.['at'])
  const urgency = parsed?.['urgency']
  if (at === null || typeof urgency !== 'string' || !(urgency in RANK)) {
    return undefined
  }
  return { at, urgency: urgency as BackupUrgency }
}

export function dismissBackupPrompt(
  storage: Storage,
  dismissal: BackupDismissal,
): Promise<void> {
  return storage.settings.set(BACKUP_DISMISSED_KEY, JSON.stringify(dismissal))
}

/** Cleared when a backup is taken, so the next escalation starts fresh. */
export function clearBackupDismissal(storage: Storage): Promise<void> {
  return storage.settings.remove(BACKUP_DISMISSED_KEY)
}
